import type { FC } from "react";
import React from "react";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { authenticationState } from "src/state/state";
import StorageActions from "./StorageActions";
import type { UserDebugInfo } from "./UserStorageTable";
import UserStorageTable from "./UserStorageTable";

export type UserCandidate = {
  userId: string | null;
  storage: string;
  priority:
    | "app_access"
    | "fullAuth"
    | "partialAuth"
    | "anonymous"
    | "noAuth"
    | "nullAuth"
    | "invalid";
  hasAppAccess: boolean;
  authStage: string | null;
  lastActiveTime: number;
};

const DebugUserStorage: FC = () => {
  const [users, setUsers] = React.useState<UserDebugInfo[]>([]);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [userSelectionResult, setUserSelectionResult] = React.useState<{
    selectedUserId: string | null;
    selectedStorage: string | null;
    allCandidates: UserCandidate[];
  } | null>(null);

  const loadUsers = React.useCallback(() => {
    const localStorageKeys = Object.keys(localStorage);
    const sessionStorageKeys = Object.keys(sessionStorage);
    const allStorageKeys = [...localStorageKeys, ...sessionStorageKeys];

    const refreshTokenRegex = /9am-([a-z0-9]+)-refreshToken/;
    const refreshTokenKeys = allStorageKeys.filter((k) =>
      k.match(refreshTokenRegex)
    );

    const userData: (UserDebugInfo | undefined)[] = [];
    const currentActiveUserId = StorageController.activeUserId;

    for (const refreshTokenKey of refreshTokenKeys) {
      const userId = refreshTokenKey.match(refreshTokenRegex)?.[1];
      if (!userId) continue;

      const storageType =
        localStorage.getItem(refreshTokenKey) !== null
          ? "localStorage"
          : "sessionStorage";
      const storage =
        storageType === "localStorage" ? localStorage : sessionStorage;
      const otherStorage =
        storageType === "localStorage" ? sessionStorage : localStorage;

      const lastActiveKey = `9am-${userId}-lastActive`;
      const lastActiveValue =
        storage.getItem(lastActiveKey) ?? Date.now().toString();
      const lastActiveTime = parseInt(lastActiveValue, 10);

      const refreshToken = storage.getItem(refreshTokenKey);
      let refreshTokenValid = false;
      let refreshTokenSub: string | null = null;

      if (refreshToken) {
        try {
          const parsed = JSON.parse(atob(refreshToken.split(".")[1])) as {
            sub?: string;
          };
          refreshTokenValid = Boolean(parsed.sub);
          refreshTokenSub = parsed.sub ?? null;
        } catch {
          refreshTokenValid = false;
        }
      }

      const authStageInPrimaryStorage = storage.getItem(
        `9am-${userId}-authStage`
      );
      const authStageInOtherStorage = otherStorage.getItem(
        `9am-${userId}-authStage`
      );
      const hasDataConflict =
        authStageInPrimaryStorage !== authStageInOtherStorage &&
        authStageInOtherStorage !== null;

      const hasAppAccess =
        storage.getItem(`9am-${userId}-hasAppAccess`) === "true";

      let priority: UserDebugInfo["priority"] = "invalid";
      if (hasAppAccess) {
        priority = "app_access";
      } else if (authStageInPrimaryStorage) {
        priority = authStageInPrimaryStorage as UserDebugInfo["priority"];
      }

      const isCurrentlyActive = refreshTokenSub === currentActiveUserId;

      userData.push({
        userId,
        refreshTokenSub,
        storage: storageType,
        lastActive: lastActiveValue,
        lastActiveTime,
        hasRefreshToken: !!refreshToken,
        refreshTokenValid,
        authStageInPrimaryStorage,
        authStageInOtherStorage,
        hasDataConflict,
        hasAppAccess,
        priority,
        isCurrentlyActive
      });
    }

    userData.filter(Boolean).sort((a, b) => {
      if (a.hasAppAccess !== b.hasAppAccess) {
        return a.hasAppAccess ? -1 : 1;
      }
      const authOrder = [
        "fullAuth",
        "partialAuth",
        "anonymous",
        "noAuth",
        "nullAuth"
      ];
      const aIndex = authOrder.indexOf(
        a.authStageInPrimaryStorage ?? "nullAuth"
      );
      const bIndex = authOrder.indexOf(
        b.authStageInPrimaryStorage ?? "nullAuth"
      );
      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }
      return b.lastActiveTime - a.lastActiveTime;
    });

    setUsers(userData.filter(Boolean));

    const bestUser =
      userData.filter(Boolean).find((u) => u.hasAppAccess) ?? userData[0];
    setUserSelectionResult({
      selectedUserId: bestUser?.refreshTokenSub ?? null,
      selectedStorage: bestUser?.storage ?? null,
      allCandidates: userData.filter(Boolean).map((u) => ({
        userId: u.refreshTokenSub,
        storage: u.storage,
        priority: u.priority,
        hasAppAccess: u.hasAppAccess,
        authStage: u.authStageInPrimaryStorage,
        lastActiveTime: u.lastActiveTime
      }))
    });
  }, [refreshKey]);

  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const logDetails = () => {
    const oneBigJson: Record<string, unknown> = {
      currentUserId: StorageController.activeUserId,
      totalUsers: users.length,
      activeStorage:
        StorageController.useStorage === localStorage
          ? "localStorage"
          : "sessionStorage",
      currentAuthStage: authenticationState.state.authStage
    };
    const localStorageKeys = Object.keys(localStorage);
    const sessionStorageKeys = Object.keys(sessionStorage);
    const allStorageKeys = [...localStorageKeys, ...sessionStorageKeys];
    allStorageKeys.forEach((key) => {
      if (key.startsWith("9am-")) {
        const value = localStorage.getItem(key) || sessionStorage.getItem(key);
        if (value) {
          const isSensitive =
            key.includes("Token") ||
            key.includes("token") ||
            key.includes("credential");
          oneBigJson[key] = {
            value: isSensitive ? "[MASKED]" : value,
            storage: localStorage.getItem(key)
              ? "localStorage"
              : "sessionStorage",
            userId: key.split("-")[1]
          };
        }
      }
    });
    // eslint-disable-next-line no-console
    console.log("All storage data:", JSON.stringify(oneBigJson, null, 2));
  };

  const moveUser = (userId: string, fromStorage: string) => {
    const confirmed = window.confirm(
      `Move user ${userId.slice(0, 8)}... from ${fromStorage} to ${fromStorage === "localStorage" ? "sessionStorage" : "localStorage"}?`
    );
    if (!confirmed) return;

    const sourceStorage =
      fromStorage === "localStorage" ? localStorage : sessionStorage;
    const targetStorage =
      fromStorage === "localStorage" ? sessionStorage : localStorage;

    const allKeys = Object.keys(sourceStorage).filter((key) =>
      key.includes(userId)
    );

    allKeys.forEach((key) => {
      const value = sourceStorage.getItem(key);
      if (value) {
        targetStorage.setItem(key, value);
        sourceStorage.removeItem(key);
      }
    });

    setRefreshKey((prev) => prev + 1);
  };

  const deleteUser = (userId: string) => {
    const confirmed = window.confirm(
      `Delete all data for user ${userId.slice(0, 8)}...? This cannot be undone.`
    );
    if (!confirmed) return;

    const keys = [...Object.keys(localStorage), ...Object.keys(sessionStorage)];
    const userKeys = keys.filter((key) => key.includes(userId));

    userKeys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    setRefreshKey((prev) => prev + 1);
  };

  const forceLogActive = () => {
    StorageController.logActive();
    setTimeout(() => {
      loadUsers();
    }, 100);
  };

  return (
    <div>
      <StorageActions
        userCount={users.length}
        userSelectionResult={userSelectionResult}
        hasDataConflict={users.some((u) => u.hasDataConflict)}
        onLoadUsers={loadUsers}
        onLogDetails={logDetails}
        onForceLogActive={forceLogActive}
      />
      <UserStorageTable
        users={users}
        onMoveUser={moveUser}
        onDeleteUser={deleteUser}
      />
    </div>
  );
};

export default DebugUserStorage;
